/* PDP clone kit – core behaviours loaded on every page (kept tiny).
   Section-specific code lives in pdp-main.js, pdp-steps.js and pdp-reviews.js and is only loaded by those sections.
   Rules: no scroll listeners for visibility, no idle animation loops, passive listeners only. */
(function () {
  'use strict';

  window.PDP = window.PDP || {};
  PDP.define = function (name, ctor) {
    if (!window.customElements || customElements.get(name)) return;
    customElements.define(name, ctor);
  };
  PDP.reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* Animated <details> used by accordions, FAQ, stats and week-by-week (delegated: one listener for the page). */
  function animateDetails(details, open) {
    if (details._pdpAnim) details._pdpAnim.cancel();
    var summary = details.querySelector(':scope > summary');
    if (!summary || PDP.reduceMotion || !details.animate) {
      details.open = open;
      return;
    }
    var start = details.offsetHeight;
    var end;
    details.style.overflow = 'hidden';
    if (open) {
      details.open = true;
      end = details.offsetHeight;
    } else {
      end = summary.offsetHeight + (details.offsetHeight - details.clientHeight);
    }
    var anim = details.animate({ height: [start + 'px', end + 'px'] }, { duration: 320, easing: 'cubic-bezier(.4,0,.2,1)' });
    details._pdpAnim = anim;
    anim.onfinish = function () {
      details._pdpAnim = null;
      if (!open) details.open = false;
      details.style.overflow = '';
    };
    anim.oncancel = function () { details._pdpAnim = null; details.style.overflow = ''; };
  }

  document.addEventListener('click', function (e) {
    var summary = e.target.closest && e.target.closest('details.pdp-anim > summary');
    if (!summary) return;
    var details = summary.parentElement;
    e.preventDefault();
    var willOpen = details._pdpAnim ? !details._pdpTarget : !details.open;
    details._pdpTarget = willOpen;
    var group = details.closest('[data-exclusive]');
    if (willOpen && group) {
      group.querySelectorAll('details.pdp-anim[open]').forEach(function (d) {
        if (d !== details && d.closest('[data-exclusive]') === group) {
          d._pdpTarget = false;
          animateDetails(d, false);
        }
      });
    }
    animateDetails(details, willOpen);
  });

  /* Header: mobile menu drawer. A "below" drawer opens right under the header: its top and the close mark's place (over
     the menu button) are measured once when it opens. */
  PDP.define('pdp-header-drawer', class extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.panel = this.querySelector('[data-drawer]');
      this.querySelectorAll('[data-drawer-open]').forEach(function (b) { b.addEventListener('click', function () { self.toggle(true); }); });
      this.querySelectorAll('[data-drawer-close]').forEach(function (b) { b.addEventListener('click', function () { self.toggle(false); }); });
      // Esc: the <dialog> asks to close; slide out instead of vanishing
      if (this.panel) this.panel.addEventListener('cancel', function (e) { e.preventDefault(); self.toggle(false); });
      if (this.dataset.stickyMode === 'scroll-up') this.initStickyUp();
    }
    toggle(open) {
      var d = this.panel;
      if (!d) return;
      clearTimeout(this._t);
      if (open) {
        var bar = this.querySelector('.pdp-header__bar');
        var burger = this.querySelector('[data-drawer-open]');
        if (bar) d.style.setProperty('--drw-top', Math.round(bar.getBoundingClientRect().bottom) + 'px');
        if (burger) {
          var r = burger.getBoundingClientRect();
          d.style.setProperty('--burger-x', Math.round(r.left) + 'px');
          d.style.setProperty('--burger-y', Math.round(r.top) + 'px');
        }
        if (!d.open) d.showModal();
        requestAnimationFrame(function () { d.classList.add('is-open'); });
      } else {
        d.classList.remove('is-open');
        this._t = setTimeout(function () { if (d.open) d.close(); }, 350);
      }
      document.documentElement.classList.toggle('pdp-lock', open);
      var opener = this.querySelector('[data-drawer-open]');
      if (opener) opener.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    /* Sticky "when scrolling up" (the theme's own header rule): once the page has scrolled past the header, scrolling
       down slides it away and scrolling up brings it back, pinned to the top; back at the header's own place it is part
       of the page again. One passive scroll listener that reads only the scroll position (no layout reads); the
       header's place is measured on load and on resize. */
    initStickyUp() {
      var sec = this.closest('.shopify-section') || this;
      var self = this;
      var top = 0, bottom = 0, last = window.pageYOffset, state = '';
      function measure() {
        var was = state;
        set('');
        var r = sec.getBoundingClientRect();
        top = r.top + window.pageYOffset;
        bottom = r.bottom + window.pageYOffset;
        set(was);
      }
      function set(next) {
        if (next === state) return;
        sec.classList.toggle('pdp-hdr-sticky', next !== '');
        sec.classList.toggle('pdp-hdr-hidden', next === 'hidden');
        // once it has slid in, hiding it again also slides (like the theme's own header); back in place: no motion
        sec.classList.toggle('pdp-hdr-animate', next === 'shown' || (next === 'hidden' && state === 'shown'));
        state = next;
      }
      measure();
      window.addEventListener('resize', measure, { passive: true });
      window.addEventListener('scroll', function () {
        var y = window.pageYOffset;
        if (self.panel && self.panel.open) { last = y; return; }
        if (y > last && y > bottom) set('hidden');
        else if (y < last && y > bottom) set('shown');
        else if (y <= top) set('');
        last = y;
      }, { passive: true });
    }
  });

  /* Carousel: a native scroll-snap track ([data-track] > [data-slide]) with optional arrows ([data-prev]/[data-next]),
     dots ([data-dots], one per slide) and a progress line ([data-progress] > span). Arrows scroll by one slide and wrap
     around at both ends. The
     active slide comes from an IntersectionObserver on the track; the progress line is updated in a scroll listener that
     only runs while the track scrolls (passive, one rAF per frame). */
  PDP.define('pdp-carousel', class extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.track = this.querySelector('[data-track]');
      if (!this.track || this._ready) return;
      this._ready = true;
      this.slides = Array.prototype.slice.call(this.track.querySelectorAll('[data-slide]'));
      this.index = 0;
      var prev = this.querySelector('[data-prev]');
      var next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', function () { self.step(-1); });
      if (next) next.addEventListener('click', function () { self.step(1); });
      this.dotsWrap = this.querySelector('[data-dots]');
      if (this.dotsWrap && this.slides.length > 1) {
        this.dots = this.slides.map(function (s, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          b.addEventListener('click', function () { self.goTo(i); });
          self.dotsWrap.appendChild(b);
          return b;
        });
      }
      this.bar = this.querySelector('[data-progress] > span');
      if (this.bar) {
        var raf = 0;
        this.track.addEventListener('scroll', function () {
          if (raf) return;
          raf = requestAnimationFrame(function () { raf = 0; self.paintBar(); });
        }, { passive: true });
        this.paintBar();
      }
      if ('IntersectionObserver' in window && this.slides.length > 1) {
        this._io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting && en.intersectionRatio >= 0.6) self.setActive(self.slides.indexOf(en.target));
          });
        }, { root: this.track, threshold: [0.6] });
        this.slides.forEach(function (s) { self._io.observe(s); });
      }
      this.setActive(0);
    }
    disconnectedCallback() { if (this._io) this._io.disconnect(); }
    setActive(i) {
      if (i < 0) return;
      this.index = i;
      if (this.dots) this.dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
    }
    goTo(i) {
      var s = this.slides[i];
      if (!s) return;
      this.track.scrollTo({ left: s.offsetLeft - this.slides[0].offsetLeft, behavior: PDP.reduceMotion ? 'auto' : 'smooth' });
      this.setActive(i);
    }
    step(d) {
      var n = this.slides.length;
      if (!n) return;
      // at an end the arrows wrap around (data-wrap: "both" by default, "desktop" = from 750 px up, "none")
      var wrap = this.dataset.wrap || 'both';
      var canWrap = wrap === 'both' || (wrap === 'desktop' && !!window.matchMedia && window.matchMedia('(min-width: 750px)').matches);
      var t = this.track, max = t.scrollWidth - t.clientWidth;
      // a snap point can sit a few pixels in from either end (track padding)
      var atStart = t.scrollLeft <= 16, atEnd = t.scrollLeft >= max - 16;
      if ((d < 0 && atStart) || (d > 0 && atEnd)) {
        if (!canWrap) return;
        if (this.dataset.step === 'page') { t.scrollTo({ left: d < 0 ? max : 0, behavior: PDP.reduceMotion ? 'auto' : 'smooth' }); return; }
        this.goTo(d < 0 ? n - 1 : 0);
        return;
      }
      if (this.dataset.step === 'page') {
        // one page (the visible width) per click
        t.scrollTo({ left: t.scrollLeft + d * t.clientWidth, behavior: PDP.reduceMotion ? 'auto' : 'smooth' });
        return;
      }
      this.goTo(Math.max(0, Math.min(n - 1, this.index + d)));
    }
    paintBar() {
      var t = this.track;
      var max = t.scrollWidth - t.clientWidth;
      var vis = t.scrollWidth ? t.clientWidth / t.scrollWidth : 1;
      var pos = max > 0 ? t.scrollLeft / max : 0;
      this.bar.style.width = (vis * 100) + '%';
      this.bar.style.transform = 'translateX(' + (pos * (1 / vis - 1) * 100) + '%)';
    }
  });

  /* Toggle buttons ([data-toggle] with aria-controls): show/hide the panel, flip aria-expanded and put is-open on the
     closest [data-toggle-scope]. One delegated listener for the page. */
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-toggle]');
    if (!b) return;
    var panel = document.getElementById(b.getAttribute('aria-controls'));
    var open = b.getAttribute('aria-expanded') !== 'true';
    b.setAttribute('aria-expanded', open ? 'true' : 'false');
    var scope = b.closest('[data-toggle-scope]');
    if (scope) scope.classList.toggle('is-open', open);
    if (panel) panel.hidden = !open;
  });

  /* [data-pdp-scrolltop]: a "back to the top" link that glides up instead of jumping (the original's button script). */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('[data-pdp-scrolltop]');
    if (!a) return;
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: PDP.reduceMotion ? 'auto' : 'smooth' });
  });

  /* Tabs ([data-tabs] > [data-tab] buttons with aria-controls, [data-tab-panel] panels): a click shows that tab's panel
     at once and hides the others. One delegated listener for the page. */
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-tab]');
    if (!b) return;
    var root = b.closest('[data-tabs]');
    if (!root) return;
    root.querySelectorAll('[data-tab]').forEach(function (t) {
      var on = t === b;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      var p = document.getElementById(t.getAttribute('aria-controls'));
      if (p) p.hidden = !on;
    });
  });

  /* Marquees and other endless CSS animations ([data-pdp-pause], e.g. a wiggling button): paused while off-screen
     (.is-paused), so they cost nothing there. A marquee with data-speed / data-speed-mobile (pixels per second) gets its
     loop time from its track: half the track is one loop, so the row moves at that speed whatever its items are. The
     time is measured again when the track changes size (fonts, images, editor changes) or the width class changes. */
  var wide = window.matchMedia ? window.matchMedia('(min-width: 750px)') : null;
  function marqueeSpeed(m) {
    var track = m.firstElementChild;
    var v = parseFloat(wide && !wide.matches ? (m.dataset.speedMobile || m.dataset.speed) : m.dataset.speed);
    if (!track || !v) return;
    var half = track.offsetWidth / 2;
    if (half > 0) track.style.setProperty('--mq-dur', (half / v).toFixed(2) + 's');
  }
  function initMarquees(root) {
    (root || document).querySelectorAll('[data-pdp-marquee][data-speed]').forEach(function (m) {
      if (m._pdpSpeed) return;
      m._pdpSpeed = true;
      marqueeSpeed(m);
      if (window.ResizeObserver && m.firstElementChild) new ResizeObserver(function () { marqueeSpeed(m); }).observe(m.firstElementChild);
      if (wide && wide.addEventListener) wide.addEventListener('change', function () { marqueeSpeed(m); });
    });
    if (!('IntersectionObserver' in window)) return;
    var holders = Array.prototype.slice.call((root || document).querySelectorAll('[data-pdp-marquee], [data-pdp-pause], .pdp-h2'));
    holders.filter(function (m) { return !m.classList.contains('pdp-h2') || m.querySelector('i, em'); }).forEach(function (m) {
      if (m._pdpObs) return;
      m._pdpObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { m.classList.toggle('is-paused', !en.isIntersecting); });
      });
      m._pdpObs.observe(m);
    });
  }

  /* Sticky announcement bar height -> --pdp-ann-h (the sticky header sits right below it). */
  function initAnnouncement(root) {
    var el = (root || document).querySelector('[data-pdp-ann][data-ann-sticky="true"]');
    if (!el || el._pdpRO || !window.ResizeObserver) return;
    el._pdpRO = new ResizeObserver(function () {
      document.documentElement.style.setProperty('--pdp-ann-h', el.offsetHeight + 'px');
    });
    el._pdpRO.observe(el);
  }

  /* In-view: [data-inview] gets .is-in once 35% of it is visible (charts that draw themselves, bars that grow). Inside it,
     [data-count] numbers count up from 0 to their own value (decimals and separator kept; data-count-ms and
     data-count-delay set the timing); text that isn't a number (e.g. a placeholder like XX.X) is left as it is. */
  function countUp(el) {
    var raw = el.textContent.trim();
    var m = raw.match(/^(\d+)([.,](\d+))?$/);
    if (!m || PDP.reduceMotion) return;
    var target = parseFloat(m[1] + (m[3] ? '.' + m[3] : ''));
    var dec = m[3] ? m[3].length : 0;
    var sep = m[2] ? m[2].charAt(0) : '.';
    var unit = Math.pow(10, -dec);                         // the smallest change the label can show
    var ms = parseFloat(el.dataset.countMs) || 1400;      // the original's duration and start delay, per figure
    var delay = parseFloat(el.dataset.countDelay) || 0;
    var show = function (v) { el.textContent = v.toFixed(dec).replace('.', sep); };
    // ease-out cubic: value(t) = target * (1 - (1 - t/ms)^3); the time a value is reached is its inverse
    var timeOf = function (v) { return ms * (1 - Math.cbrt(Math.max(0, 1 - v / target))); };
    var t0 = 0;
    // no frame loop (LEARNINGS rule 21): the label changes only when its shown number changes, from a timer due then
    function tick() {
      var t = performance.now() - t0;
      if (t >= ms) { el.textContent = raw; return; }
      var v = target * (1 - Math.pow(1 - t / ms, 3));
      var shown = Math.floor(v / unit + 1e-9) * unit;
      show(shown);
      setTimeout(tick, Math.max(1, timeOf(Math.min(target, shown + unit)) - t));
    }
    show(0);
    setTimeout(function () { t0 = performance.now(); tick(); }, delay);
  }
  function initInView(root) {
    var els = (root || document).querySelectorAll('[data-inview]:not(.is-in)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(function (el) { el.classList.add('is-in'); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-in');
        en.target.querySelectorAll('[data-count]').forEach(countUp);
        io.unobserve(en.target);
      });
    }, { threshold: 0.35 });
    els.forEach(function (el) { io.observe(el); });
  }

  function init(root) { initMarquees(root); initAnnouncement(root); initInView(root); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
